import { db, auth } from '../firebase/config';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  query, 
  where, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  setDoc,
  limit
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import Certificate from '../models/Certificate';
import sampleCertificates from '../data/sampleCertificates'; // Keep for fallback

class FirebaseService {
  // Collection references
  certificatesRef = collection(db, 'certificates');
  usersRef = collection(db, 'users');
  accreditedOrgsRef = collection(db, 'accreditedOrganizations');
  verifiedPersonnelRef = collection(db, 'verifiedPersonnel');
  
  // Fallback to sample data if Firebase fetch fails
  certificates = sampleCertificates;
  
  // Cache configuration
  certificatesCache = {
    data: null,
    lastUpdated: null,
    cacheExpiration: 24 * 60 * 60 * 1000 // 24 hours in milliseconds
  };
  
  organizationsCache = {
    data: null,
    lastUpdated: null,
    cacheExpiration: 24 * 60 * 60 * 1000 // 24 hours in milliseconds
  };
  
  personnelCache = {
    data: null,
    lastUpdated: null,
    cacheExpiration: 24 * 60 * 60 * 1000 // 24 hours in milliseconds
  };
  
  // Current authenticated user
  currentUser = null;
  
  constructor() {
    // Set up auth state listener with persistence
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
      if (user) {
        // Store minimal user data in localStorage for persistence
        localStorage.setItem('authUser', JSON.stringify({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName
        }));
      } else {
        // Remove user data when logged out
        localStorage.removeItem('authUser');
      }
    });

    // Try to restore user from localStorage
    const storedUser = localStorage.getItem('authUser');
    if (storedUser) {
      this.currentUser = JSON.parse(storedUser);
    }

    // Try to restore certificates cache from localStorage
    const storedCertificatesCache = localStorage.getItem('certificatesCache');
    if (storedCertificatesCache) {
      try {
        const parsedCache = JSON.parse(storedCertificatesCache);
        this.certificatesCache.data = parsedCache.data;
        this.certificatesCache.lastUpdated = parsedCache.lastUpdated;
        // Certificates cache restored from localStorage
      } catch (e) {
        // Failed to parse certificates cache
      }
    }
    
    // Try to restore organizations cache from localStorage
    const storedOrganizationsCache = localStorage.getItem('organizationsCache');
    if (storedOrganizationsCache) {
      try {
        const parsedCache = JSON.parse(storedOrganizationsCache);
        this.organizationsCache.data = parsedCache.data;
        this.organizationsCache.lastUpdated = parsedCache.lastUpdated;
        // Organizations cache restored from localStorage
      } catch (e) {
        // Failed to parse organizations cache
      }
    }
    
    // Try to restore personnel cache from localStorage
    const storedPersonnelCache = localStorage.getItem('personnelCache');
    if (storedPersonnelCache) {
      try {
        const parsedCache = JSON.parse(storedPersonnelCache);
        this.personnelCache.data = parsedCache.data;
        this.personnelCache.lastUpdated = parsedCache.lastUpdated;
        // Personnel cache restored from localStorage
      } catch (e) {
        // Failed to parse personnel cache
      }
    }

    // Set auth persistence
    setPersistence(auth, browserLocalPersistence).catch(error => {
      // Error setting auth persistence
    });
  }
  
  // Authentication Methods
  registerWithEmailAndPassword = async (email, password, name) => {
    try {
      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update user profile with name
      await updateProfile(user, {
        displayName: name
      });
      
      // Store additional user data in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        name,
        email,
        role: 'user',
        createdAt: new Date().toISOString()
      });
      
      return user;
    } catch (error) {
      // Error registering user
      throw error;
    }
  };
  
  loginWithEmailAndPassword = async (email, password, persist = true) => {
    try {
      // Set persistence to LOCAL if requested (default)
      if (persist) {
        await setPersistence(auth, browserLocalPersistence);
      }
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      // Error logging in
      throw error;
    }
  };
  
  logout = async () => {
    try {
      await signOut(auth);
      return true;
    } catch (error) {
      // Error logging out
      throw error;
    }
  };
  
  sendPasswordResetEmail = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      // Error sending password reset email
      throw error;
    }
  };
  
  getCurrentUser = () => {
    return auth.currentUser;
  };
  
  isAuthenticated = () => {
    return !!auth.currentUser;
  };
  
  onAuthStateChanged = (callback) => {
    return onAuthStateChanged(auth, callback);
  };
  
  getUserData = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      // Error getting user data
      throw error;
    }
  };

  // Verified Personnel methods
  async getAllVerifiedPersonnel(forceRefresh = false) {
    try {
      // Check if we have valid cached data
      const now = Date.now();
      const cacheIsValid = 
        this.personnelCache.data && 
        this.personnelCache.lastUpdated && 
        (now - this.personnelCache.lastUpdated < this.personnelCache.cacheExpiration);
      
      // Use cache if valid and not forcing refresh
      if (cacheIsValid && !forceRefresh) {
        // Using cached personnel data
        return this.personnelCache.data;
      }
      
      // Otherwise fetch from Firestore
      // Fetching personnel data from Firestore
      const snapshot = await getDocs(this.verifiedPersonnelRef);
      const personnel = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Update cache
      this.personnelCache.data = personnel;
      this.personnelCache.lastUpdated = now;
      
      // Store in localStorage for persistence
      localStorage.setItem('personnelCache', JSON.stringify({
        data: personnel,
        lastUpdated: now
      }));
      
      return personnel;
    } catch (error) {
      // Error getting verified personnel
      
      // Return cached data if available, even if expired
      if (this.personnelCache.data) {
        // Returning cached personnel data after fetch error
        return this.personnelCache.data;
      }
      
      return [];
    }
  }
  
  async addVerifiedPersonnel(personnelData) {
    try {
      const docRef = await addDoc(this.verifiedPersonnelRef, personnelData);
      const newPersonnel = { id: docRef.id, ...personnelData };
      
      // Update cache if it exists
      if (this.personnelCache.data) {
        this.personnelCache.data.push(newPersonnel);
        this.personnelCache.lastUpdated = Date.now();
        
        // Update localStorage
        localStorage.setItem('personnelCache', JSON.stringify({
          data: this.personnelCache.data,
          lastUpdated: this.personnelCache.lastUpdated
        }));
      }
      
      return newPersonnel;
    } catch (error) {
      // Error adding verified personnel
      throw error;
    }
  }
  
  async updateVerifiedPersonnel(id, personnelData) {
    try {
      const personnelRef = doc(this.verifiedPersonnelRef, id);
      await updateDoc(personnelRef, personnelData);
      const updatedPersonnel = { id, ...personnelData };
      
      // Update cache if it exists
      if (this.personnelCache.data) {
        const index = this.personnelCache.data.findIndex(p => p.id === id);
        if (index !== -1) {
          // Preserve any fields not included in the update
          this.personnelCache.data[index] = {
            ...this.personnelCache.data[index],
            ...personnelData
          };
          this.personnelCache.lastUpdated = Date.now();
          
          // Update localStorage
          localStorage.setItem('personnelCache', JSON.stringify({
            data: this.personnelCache.data,
            lastUpdated: this.personnelCache.lastUpdated
          }));
        }
      }
      
      return updatedPersonnel;
    } catch (error) {
      // Error updating verified personnel
      throw error;
    }
  }
  
  async deleteVerifiedPersonnel(id) {
    try {
      const personnelRef = doc(this.verifiedPersonnelRef, id);
      await deleteDoc(personnelRef);
      
      // Update cache if it exists
      if (this.personnelCache.data) {
        this.personnelCache.data = this.personnelCache.data.filter(p => p.id !== id);
        this.personnelCache.lastUpdated = Date.now();
        
        // Update localStorage
        localStorage.setItem('personnelCache', JSON.stringify({
          data: this.personnelCache.data,
          lastUpdated: this.personnelCache.lastUpdated
        }));
      }
      
      return true;
    } catch (error) {
      // Error deleting verified personnel
      throw error;
    }
  }
  
  // Accredited Organizations methods
  async getAllAccreditedOrganizations(forceRefresh = false) {
    try {
      // Check if we have a valid cache
      const now = Date.now();
      const cacheIsValid = 
        this.organizationsCache.data && 
        this.organizationsCache.lastUpdated && 
        (now - this.organizationsCache.lastUpdated < this.organizationsCache.cacheExpiration);
      
      // Use cache if valid and not forcing refresh
      if (cacheIsValid && !forceRefresh) {
        // Using cached organizations data
        return this.organizationsCache.data;
      }
      
      // Otherwise fetch from Firestore
      // Fetching organizations from Firestore
      const snapshot = await getDocs(this.accreditedOrgsRef);
      const organizations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Update cache
      this.organizationsCache.data = organizations;
      this.organizationsCache.lastUpdated = now;
      
      // Store in localStorage for persistence
      localStorage.setItem('organizationsCache', JSON.stringify({
        data: organizations,
        lastUpdated: now
      }));
      
      return organizations;
    } catch (error) {
      // Error getting accredited organizations
      
      // Return cache as fallback if available
      if (this.organizationsCache.data) {
        // Using cached organizations data as fallback after error
        return this.organizationsCache.data;
      }
      
      return [];
    }
  }
  
  async addAccreditedOrganization(orgData) {
    try {
      const docRef = await addDoc(this.accreditedOrgsRef, orgData);
      const newOrg = { id: docRef.id, ...orgData };
      
      // Update cache if it exists
      if (this.organizationsCache.data) {
        this.organizationsCache.data.push(newOrg);
        this.organizationsCache.lastUpdated = Date.now();
        
        // Update localStorage
        localStorage.setItem('organizationsCache', JSON.stringify({
          data: this.organizationsCache.data,
          lastUpdated: this.organizationsCache.lastUpdated
        }));
      }
      
      return newOrg;
    } catch (error) {
      // Error adding accredited organization
      throw error;
    }
  }
  
  async updateAccreditedOrganization(id, orgData) {
    try {
      const orgRef = doc(db, 'accreditedOrganizations', id);
      await updateDoc(orgRef, orgData);
      const updatedOrg = { id, ...orgData };
      
      // Update cache if it exists
      if (this.organizationsCache.data) {
        const index = this.organizationsCache.data.findIndex(org => org.id === id);
        if (index !== -1) {
          // Preserve fields that weren't updated
          this.organizationsCache.data[index] = {
            ...this.organizationsCache.data[index],
            ...orgData
          };
          this.organizationsCache.lastUpdated = Date.now();
          
          // Update localStorage
          localStorage.setItem('organizationsCache', JSON.stringify({
            data: this.organizationsCache.data,
            lastUpdated: this.organizationsCache.lastUpdated
          }));
        }
      }
      
      return updatedOrg;
    } catch (error) {
      // Error updating accredited organization
      throw error;
    }
  }
  
  async deleteAccreditedOrganization(id) {
    try {
      const orgRef = doc(db, 'accreditedOrganizations', id);
      await deleteDoc(orgRef);
      
      // Update cache if it exists
      if (this.organizationsCache.data) {
        this.organizationsCache.data = this.organizationsCache.data.filter(org => org.id !== id);
        this.organizationsCache.lastUpdated = Date.now();
        
        // Update localStorage
        localStorage.setItem('organizationsCache', JSON.stringify({
          data: this.organizationsCache.data,
          lastUpdated: this.organizationsCache.lastUpdated
        }));
      }
      
      return true;
    } catch (error) {
      // Error deleting accredited organization
      throw error;
    }
  }
  
  // Methods for public organization verification
  async getOrganizationByCertificateNumber(certificateNumber) {
    try {
      if (!certificateNumber) return null;
      
      // Create a query against the certi_no field
      const q = query(
        this.accreditedOrgsRef,
        where('certi_no', '==', certificateNumber.trim()),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // No organization found with the given certificate number
        return null;
      }
      
      // Return the first matching organization
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    } catch (error) {
      // Error getting organization by certificate number
      throw error;
    }
  }
  
  async searchOrganizationsByName(name) {
    try {
      if (!name) return [];
      
      // Normalize the search term
      const searchTerm = name.trim().toLowerCase();
      
      // For Firestore, we need to use startAt and endAt with a field that we can query
      // Since Firestore doesn't support native case-insensitive search, we'll fetch and filter
      const snapshot = await getDocs(this.accreditedOrgsRef);
      
      if (snapshot.empty) {
        return [];
      }
      
      // Filter the results client-side
      const results = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(org => {
          const orgName = (org.org_name || '').toLowerCase();
          return orgName.includes(searchTerm);
        });
      
      return results;
    } catch (error) {
      // Error searching organizations by name
      throw error;
    }
  }
  
  // Methods for public personnel verification
  async getPersonnelByCertificateNumber(certificateNumber) {
    try {
      if (!certificateNumber) return null;
      
      // Require at least 3 characters to prevent data exposure
      const trimmedCertNumber = certificateNumber.trim();
      if (trimmedCertNumber.length < 3) {
        // Removed console.warn
        return null;
      }
      
      // Create a query against the certi_no field
      const q = query(
        this.verifiedPersonnelRef,
        where('certi_no', '==', trimmedCertNumber),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // No personnel found with the given certificate number
        return null;
      }
      
      // Return the first matching personnel
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    } catch (error) {
      // Removed console.error
      throw error;
    }
  }
  
  async searchPersonnelByName(name) {
    try {
      if (!name) return [];
      
      // Normalize the search term
      const searchTerm = name.trim().toLowerCase();
      
      // Require at least 3 characters to prevent data exposure
      if (searchTerm.length < 3) {
        // Removed console.warn
        return [];
      }
      
      // For Firestore, we need to use startAt and endAt with a field that we can query
      // Since Firestore doesn't support native case-insensitive search, we'll fetch and filter
      const snapshot = await getDocs(this.verifiedPersonnelRef);
      
      if (snapshot.empty) {
        return [];
      }
      
      // Filter the results client-side
      const results = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(personnel => {
          const auditorName = (personnel.auditor_name || '').toLowerCase();
          return auditorName.includes(searchTerm);
        });
      
      return results;
    } catch (error) {
      // Removed console.error
      throw error;
    }
  }

  // Get the last update timestamp for certificates collection
  getLastCertificateUpdateTimestamp = async () => {
    try {
      // Check if there's a metadata document in Firestore that tracks last update
      const metadataRef = doc(db, 'metadata', 'certificates');
      const metadataSnap = await getDoc(metadataRef);
      
      if (metadataSnap.exists()) {
        return metadataSnap.data().lastUpdated;
      }
      
      // If no metadata document exists, create one with current timestamp
      const currentTimestamp = new Date().getTime();
      await setDoc(metadataRef, { lastUpdated: currentTimestamp });
      return currentTimestamp;
    } catch (error) {
      // Removed console.error
      return null;
    }
  }

  // Update the last update timestamp for certificates collection
  updateCertificateTimestamp = async () => {
    try {
      const metadataRef = doc(db, 'metadata', 'certificates');
      await setDoc(metadataRef, { lastUpdated: new Date().getTime() });
    } catch (error) {
      // Removed console.error
    }
  }
  
  // Get all certificates with caching
  getAllCertificates = async (forceRefresh = false) => {
    try {
      const now = new Date().getTime();
      
      // Check if we have cached data and if it's still valid
      if (!forceRefresh && 
          this.certificatesCache.data && 
          this.certificatesCache.lastUpdated && 
          (now - this.certificatesCache.lastUpdated < this.certificatesCache.cacheExpiration)) {
        
        // Check if there's been an update on the server
        const serverTimestamp = await this.getLastCertificateUpdateTimestamp();
        
        // If server timestamp is newer than our cache or there's no server timestamp, refresh the data
        if (!serverTimestamp || serverTimestamp <= this.certificatesCache.lastUpdated) {
          // Removed console.log
          return this.certificatesCache.data;
        }
      }
      
      // If we get here, we need to fetch fresh data
      // Removed console.log
      const snapshot = await getDocs(this.certificatesRef);
      
      if (snapshot.empty) {
        // Removed console.log
        return this.certificates;
      }
      
      // Process the data
      const certificatesData = snapshot.docs.map(doc => {
        const data = doc.data();
        return new Certificate(
          doc.id,
          data.certificate_no,
          data.company_name,
          data.company_address,
          data.issue_date,
          data.expiry_date,
          data.company_type,
          data.scope,
          data.first_date,
          data.second_date,
          data.status,
          data.certificate_standard,
          data.created_at
        );
      });
      
      // Update the cache
      this.certificatesCache.data = certificatesData;
      this.certificatesCache.lastUpdated = now;
      
      // Store in localStorage for persistence across page refreshes
      try {
        localStorage.setItem('certificatesCache', JSON.stringify({
          data: certificatesData,
          lastUpdated: now
        }));
      } catch (e) {
        // Removed console.warn
      }
      
      return certificatesData;
    } catch (error) {
      // Removed console.error
      // Removed console.log
      return this.certificates;
    }
  }

  // Get certificate by ID
  getCertificateById = async (id) => {
    try {
      const docRef = doc(db, 'certificates', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return new Certificate(
          docSnap.id,
          data.certificate_no,
          data.company_name,
          data.issue_date,
          data.expiry_date,
          data.certification_scope,
          data.status,
          data.logo_url
        );
      } else {
        // Try to find in sample data if not in Firestore
        const certificate = this.certificates.find(cert => cert.id === id);
        return certificate || null;
      }
    } catch (error) {
      // Removed console.error
      // Fallback to sample data
      const certificate = this.certificates.find(cert => cert.id === id);
      return certificate || null;
    }
  }

  // Get certificate by number - for public verification
  getCertificateByNumber = async (certificateNumber) => {
    try {
      if (!certificateNumber) return null;
      
      // Clean up the certificate number (remove spaces, convert to uppercase)
      const cleanCertNumber = certificateNumber.trim().toUpperCase();
      
      // Require at least 3 characters to prevent data exposure
      if (cleanCertNumber.length < 3) {
        // Removed console.warn
        return null;
      }
      
      // Query Firestore for the exact certificate number
      const q = query(this.certificatesRef, where("certificate_no", "==", cleanCertNumber));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // Certificate found in Firestore
        const certData = snapshot.docs[0].data();
        return { 
          id: snapshot.docs[0].id, 
          ...certData
        };
      }
      
      // Try case-insensitive search if no exact match
      const allCertsQuery = query(this.certificatesRef);
      const allCertsSnapshot = await getDocs(allCertsQuery);
      
      // Search for case-insensitive match
      const matchingCert = allCertsSnapshot.docs.find(doc => {
        const data = doc.data();
        return data.certificate_no && 
               data.certificate_no.trim().toUpperCase() === cleanCertNumber;
      });
      
      if (matchingCert) {
        const certData = matchingCert.data();
        return { 
          id: matchingCert.id, 
          ...certData
        };
      }
      
      // If still not found, try sample data as fallback
      // Removed console.log
      const sampleCert = this.certificates.find(cert => 
        cert.certificate_no && cert.certificate_no.trim().toUpperCase() === cleanCertNumber
      );
      
      return sampleCert || null;
    } catch (error) {
      // Removed console.error
      // Fallback to sample data in case of error
      const cleanCertNumber = certificateNumber.trim().toUpperCase();
      const sampleCert = this.certificates.find(cert => 
        cert.certificate_no && cert.certificate_no.trim().toUpperCase() === cleanCertNumber
      );
      return sampleCert || null;
    }
  };

  // Add a new certificate
  addCertificate = async (certificate) => {
    try {
      const docRef = await addDoc(this.certificatesRef, {
        certificate_no: certificate.certificate_no,
        company_name: certificate.company_name,
        company_address: certificate.company_address,
        issue_date: certificate.issue_date,
        expiry_date: certificate.expiry_date,
        company_type: certificate.company_type,
        scope: certificate.scope,
        first_date: certificate.first_date,
        second_date: certificate.second_date,
        status: certificate.status,
        certificate_standard: certificate.certificate_standard,
        created_at: new Date().toISOString()
      });
      
      // Update the timestamp to indicate data has changed
      await this.updateCertificateTimestamp();
      
      // Invalidate the cache
      this.certificatesCache.lastUpdated = null;
      
      return docRef.id;
    } catch (error) {
      // Removed console.error
      throw error;
    }
  }

  // Add a certificate with fallback
  addCertificateWithFallback = async (certificate) => {
    try {
      const docRef = await addDoc(this.certificatesRef, {
        certificate_no: certificate.certificate_no,
        company_name: certificate.company_name,
        company_address: certificate.company_address,
        issue_date: certificate.issue_date,
        expiry_date: certificate.expiry_date,
        company_type: certificate.company_type,
        scope: certificate.scope,
        first_date: certificate.first_date,
        second_date: certificate.second_date,
        status: certificate.status,
        certificate_standard: certificate.certificate_standard
      });
      
      // Update the timestamp to indicate data has changed
      await this.updateCertificateTimestamp();
      
      // Invalidate the cache
      this.certificatesCache.lastUpdated = null;
      
      return docRef.id;
    } catch (error) {
      // Removed console.error
      
      // Fallback to sample data
      try {
        const newCertificate = {
          id: Date.now().toString(),
          certificate_no: certificate.certificate_no,
          company_name: certificate.company_name,
          company_address: certificate.company_address,
          issue_date: certificate.issue_date,
          expiry_date: certificate.expiry_date,
          company_type: certificate.company_type,
          scope: certificate.scope,
          first_date: certificate.first_date,
          second_date: certificate.second_date,
          status: certificate.status,
          certificate_standard: certificate.certificate_standard
        };
        
        this.certificates.push(newCertificate);
        return newCertificate.id;
      } catch (fallbackError) {
        // Removed console.error
        throw fallbackError;
      }
    }
  }

  // Search certificates by company name - optimized for public verification
  searchCertificatesByCompany = async (companyName) => {
    try {
      if (!companyName || companyName.trim() === '') {
        return [];
      }
      
      // Clean up the company name
      const searchTerm = companyName.trim().toLowerCase();
      
      // Require at least 3 characters to prevent data exposure
      if (searchTerm.length < 3) {
        return [];
      }
      
      // SIMPLIFIED APPROACH: Get ALL certificates (with a reasonable limit)
      // and then filter them client-side
      const certificatesQuery = query(this.certificatesRef, limit(1000));
      const snapshot = await getDocs(certificatesQuery);
      
      // First check Firestore data
      if (!snapshot.empty) {
        const matchingCerts = [];
        
        // Process each document
        snapshot.forEach(doc => {
          const data = doc.data();
          // Only include if company_name exists and contains the search term
          if (data.company_name && 
              data.company_name.toLowerCase().includes(searchTerm)) {
            matchingCerts.push({
              id: doc.id,
              certificate_no: data.certificate_no || '',
              company_name: data.company_name || '',
              company_address: data.company_address || '',
              issue_date: data.issue_date || '',
              expiry_date: data.expiry_date || '',
              company_type: data.company_type || '',
              scope: data.scope || '',
              status: data.status,
              certificate_standard: data.certificate_standard || ''
            });
          }
        });
        
        // If we found matches in Firestore, return them
        if (matchingCerts.length > 0) {
          return matchingCerts;
        }
      }
      
      // If no matches in Firestore, check sample data as fallback
      return this.certificates
        .filter(cert => cert.company_name && 
                cert.company_name.toLowerCase().includes(searchTerm))
        .slice(0, 20)
        .map(cert => ({
          ...cert,
          status: typeof cert.status === 'boolean' ? cert.status : 
                 cert.status === 'active' ? true : 
                 cert.status === 'inactive' ? false : true
        }));
    } catch (error) {
      return [];
    }
  };

  // Update a certificate
  updateCertificate = async (id, certificate) => {
    try {
      const certificateRef = doc(this.certificatesRef, id);
      await updateDoc(certificateRef, {
        certificate_no: certificate.certificate_no,
        company_name: certificate.company_name,
        company_address: certificate.company_address,
        issue_date: certificate.issue_date,
        expiry_date: certificate.expiry_date,
        company_type: certificate.company_type,
        scope: certificate.scope,
        first_date: certificate.first_date,
        second_date: certificate.second_date,
        status: certificate.status,
        certificate_standard: certificate.certificate_standard,
        certify_text: certificate.certify_text,
        updated_at: certificate.updated_at || new Date().toISOString()
      });
      
      // Update the timestamp to indicate data has changed
      await this.updateCertificateTimestamp();
      
      // Invalidate the cache
      this.certificatesCache.lastUpdated = null;
      
      return id;
    } catch (error) {
      // Removed console.error
      throw error;
    }
  }

  // Delete a certificate
  deleteCertificate = async (id) => {
    try {
      const certificateRef = doc(this.certificatesRef, id);
      await deleteDoc(certificateRef);
      
      // Update the timestamp to indicate data has changed
      await this.updateCertificateTimestamp();
      
      // Invalidate the cache
      this.certificatesCache.lastUpdated = null;
      
      return true;
    } catch (error) {
      // Removed console.error
      throw error;
    }
  }

  // Delete a verified personnel
  deleteVerifiedPersonnel = async (id) => {
    try {
      const docRef = doc(db, 'verifiedPersonnel', id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      // Removed console.error
      
      // Fallback to sample data
      try {
        const index = this.samplePersonnel.findIndex(person => person.id === id);
        if (index !== -1) {
          this.samplePersonnel.splice(index, 1);
          return true;
        }
        return false;
      } catch (fallbackError) {
        // Removed console.error
        throw fallbackError;
      }
    }
  }

  // Initialize Firestore with sample data if needed
  async initializeFirestoreWithSampleData() {
    try {
      // Check if certificates collection is empty
      const certificatesSnapshot = await getDocs(this.certificatesRef);
      
      if (certificatesSnapshot.empty) {
        // Removed console.log
        
        // Add sample certificates to Firestore
        for (const cert of sampleCertificates) {
          await addDoc(this.certificatesRef, cert);
        }
        
        // Removed console.log
      }
      
      // Check if accredited organizations collection is empty
      const orgsSnapshot = await getDocs(this.accreditedOrgsRef);
      
      if (orgsSnapshot.empty) {
        // Removed console.log
        
        // Sample accredited organizations data
        const sampleOrgs = [
          {
            org_name: 'NIRMAAN SCHOOL OF DESIGN AND ARTS (UNIT OF NIRMAAN INSTITUTE)',
            certi_no: 'EUAS/IN/ACCR/128M/3987',
            city: 'MUMBAI',
            state: 'MAHARASHTRA',
            country: 'REPUBLIC OF INDIA',
            program: 'ISO/IEC 17024-1:2012',
            status: 'ACCREDITED',
            exp_date: '2028-05-20'
          },
          {
            org_name: 'INFOCOST SOLUTION PRIVATE LIMITED',
            certi_no: 'EUAS/IN/INST/4429/097',
            city: 'NAVI MUMBAI',
            state: 'MAHARASHTRA',
            country: 'INDIA',
            program: 'ISO/IEC 17024-1:2012',
            status: 'ACCREDITED',
            exp_date: '2028-04-09'
          },
          {
            org_name: 'UT GLOBAL EDVANCE CERTIFICATION INC',
            certi_no: 'EUAS/IN/ACCP/4864/0137',
            city: 'MUMBAI',
            state: 'MAHARASHTRA',
            country: 'INDIA',
            program: 'ISO/IEC 17021-1:2015',
            status: 'ACCREDITED',
            exp_date: '2025-07-23'
          },
          {
            org_name: 'TSN CERTIFICATION PRIVATE LIMITED',
            certi_no: 'EUAS/IN/ACCP/4484/0369',
            city: 'LUCKNOW',
            state: 'UTTAR PRADESH',
            country: 'India',
            program: 'ISO/IEC 17021-1:2015',
            status: 'ACCREDITED',
            exp_date: '2025-06-10'
          },
          {
            org_name: 'LABTECH GLOBAL',
            certi_no: 'IN/ACCR/1736/1907',
            city: 'BALESHWAR',
            state: 'GUJARAT',
            country: 'INDIA',
            program: 'ISO/IEC 17025:2017',
            status: 'ACCREDITED',
            exp_date: '2025-03-04'
          }
        ];
        
        // Add sample accredited organizations to Firestore
        for (const org of sampleOrgs) {
          await addDoc(this.accreditedOrgsRef, org);
        }
        
        // Removed console.log
      }
      
      // Check if verified personnel collection is empty
      const personnelSnapshot = await getDocs(this.verifiedPersonnelRef);
      
      if (personnelSnapshot.empty) {
        // Removed console.log
        
        // Sample verified personnel data
        const samplePersonnel = [
          // {
          //   auditor_name: 'Mr. ZAID MOHAMMAD HARIS DIWAN',
          //   certi_no: 'IN/LA/166M2702',
          //   issue_date: '2025-02-12',
          //   country: 'INDIA',
          //   status: 'Certified',
          //   certificate_standard: 'OHSAS ISO 45001:2018'
          // },
          // {
          //   auditor_name: 'Mr. ZAID MOHAMMAD HARIS DIWAN',
          //   certi_no: 'IN/LA/165M2702',
          //   issue_date: '2025-02-12',
          //   country: 'INDIA',
          //   status: 'Certified',
          //   certificate_standard: 'OHSAS ISO 45001:2018'
          // },
          // {
          //   auditor_name: 'Mr. ZAID MOHAMMAD HARIS DIWAN',
          //   certi_no: 'IN/LA/164M7025',
          //   issue_date: '2025-02-12',
          //   country: 'INDIA',
          //   status: 'Certified',
          //   certificate_standard: 'OHSAS ISO 45001:2018'
          // },
          // {
          //   auditor_name: 'DEEPENDRA SUTHAR',
          //   certi_no: 'IN/220/M45',
          //   issue_date: '2023-09-11',
          //   country: 'INDIA',
          //   status: 'ACTIVE',
          //   certificate_standard: 'OHSAS ISO 45001:2018'
          // },
          // {
          //   auditor_name: 'DEEPENDRA SUTHAR',
          //   certi_no: 'IN/261/M459',
          //   issue_date: '2023-09-11',
          //   country: 'INDIA',
          //   status: 'ACTIVE',
          //   certificate_standard: 'OHSAS ISO 45001:2018'
          // }
        ];
        
        // Add sample verified personnel to Firestore
        for (const personnel of samplePersonnel) {
          await addDoc(this.verifiedPersonnelRef, personnel);
        }
        
        // Removed console.log
      } 
      return true;
    } catch (error) {
      // Removed console.error
      return false;
    }
  }
}

// Create an instance of the service
const firebaseService = new FirebaseService();

// Export the instance
export default firebaseService;
